/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * Layout Engine
 * Calculates positions for nodes in the DAS topology hierarchy
 * Based on custom hierarchical layout algorithm from UX mockup
 */

import { TopologyNode, TopologyEdge, NodePosition, LayoutConfig } from "./types";
import { LAYOUT_CONFIG, NODE_TYPE_CONFIG } from "./constants";

interface LayoutResult {
  positionedNodes: Map<string, NodePosition>;
  edges: TopologyEdge[];
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

/**
 * Compute hierarchical layout for topology tree
 * Returns positioned nodes and edges
 */
export function computeLayout(
  root: TopologyNode | null,
  collapsedNodeIds: Set<string>,
  config: LayoutConfig = LAYOUT_CONFIG
): LayoutResult {
  const positionedNodes = new Map<string, NodePosition>();
  const edges: TopologyEdge[] = [];
  
  if (!root) {
    return {
      positionedNodes,
      edges,
      bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
    };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  /**
   * Recursive layout function
   * Returns the width of the subtree
   */
  function layoutNode(
    node: TopologyNode,
    x: number,
    y: number,
    parent: TopologyNode | null
  ): number {
    // Position this node
    positionedNodes.set(node.id, { x, y });

    // Update bounds
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);

    // Create edge to parent
    if (parent) {
      edges.push({
        id: `${parent.id}-${node.id}`,
        source: parent.id,
        target: node.id,
      });
    }

    // If node is collapsed, don't layout children
    if (collapsedNodeIds.has(node.id) || node.children.length === 0) {
      const nodeConfig = NODE_TYPE_CONFIG[node.type];
      return nodeConfig?.width || config.nodeWidth;
    }

    // Layout children (horizontal left-to-right layout)
    const nodeConfig = NODE_TYPE_CONFIG[node.type];
    const nodeWidth = nodeConfig?.width || config.nodeWidth;
    const nodeHeight = nodeConfig?.height || config.nodeHeight;
    const childX = x + nodeWidth + config.xGap;  // Children to the right
    let childY = y;
    let totalHeight = 0;

    node.children.forEach((child: TopologyNode, index: number) => {
      const childHeight = layoutNode(child, childX, childY, node);
      
      if (index < node.children.length - 1) {
        childY += childHeight + config.yGap;
        totalHeight += childHeight + config.yGap;
      } else {
        totalHeight += childHeight;
      }
    });

    // Center parent vertically over children if children are taller
    if (totalHeight > nodeHeight) {
      const offset = (totalHeight - nodeHeight) / 2;
      const currentPos = positionedNodes.get(node.id)!;
      positionedNodes.set(node.id, { x: currentPos.x, y: currentPos.y + offset });
      return totalHeight;
    }

    return nodeHeight;
  }

  // Start layout from root at (0, 0)
  layoutNode(root, 0, 0, null);

  return {
    positionedNodes,
    edges,
    bounds: { minX, maxX, minY, maxY },
  };
}

/**
 * Filter edges based on collapsed nodes
 * Edges to/from hidden nodes are not shown
 */
export function filterVisibleEdges(
  edges: TopologyEdge[],
  collapsedNodeIds: Set<string>,
  nodeMap: Map<string, TopologyNode>
): TopologyEdge[] {
  return edges.filter((edge) => {
    // Check if any ancestor of target is collapsed
    let currentId: string | undefined = edge.target;
    
    while (currentId) {
      const node = nodeMap.get(currentId);
      if (!node) break;
      
      // Find parent
      let parentId: string | undefined;
      nodeMap.forEach((n: TopologyNode, id: string) => {
        if (n.children.some((c: TopologyNode) => c.id === currentId)) {
          parentId = id;
        }
      });
      
      if (parentId && collapsedNodeIds.has(parentId)) {
        return false; // Hide edge if ancestor is collapsed
      }
      
      currentId = parentId;
    }
    
    return true;
  });
}

/**
 * Build a flat map of all nodes for quick lookup
 */
export function buildNodeMap(root: TopologyNode | null): Map<string, TopologyNode> {
  const nodeMap = new Map<string, TopologyNode>();
  
  if (!root) return nodeMap;
  
  function walk(node: TopologyNode) {
    nodeMap.set(node.id, node);
    node.children.forEach(walk);
  }
  
  walk(root);
  return nodeMap;
}

/**
 * Get all visible node IDs (not hidden by collapsed ancestors)
 */
export function getVisibleNodeIds(
  root: TopologyNode | null,
  collapsedNodeIds: Set<string>
): Set<string> {
  const visibleIds = new Set<string>();
  
  if (!root) return visibleIds;
  
  function walk(node: TopologyNode, ancestorCollapsed: boolean) {
    if (ancestorCollapsed) return;
    
    visibleIds.add(node.id);
    
    const isCollapsed = collapsedNodeIds.has(node.id);
    node.children.forEach((child: TopologyNode) => walk(child, isCollapsed));
  }
  
  walk(root, false);
  return visibleIds;
}
